import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Spinner } from '@/components/ui/spinner'
import { jobsApi } from '@/services/jobs'
import crewsApi from '@/services/crews'
import { categoriesApi } from '@/services/categories'
import { locationsApi } from '@/services/locations'
import { workersApi } from '@/services/workers'
import { orgUsersApi } from '@/services/org-users'
import { orgTreeApi } from '@/services/org-tree'
import { Job } from '@/types'
import { getErrorMessage } from '@/lib/api'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { OrderSelector } from '@/components/orders/order-selector'
import { JobScheduleFields, bindJobScheduleFormFields } from '@/components/jobs/job-schedule-fields'
import { JobAcceptanceModeFields, bindJobAcceptanceModeFormFields } from '@/components/jobs/job-acceptance-mode-fields'
import { JobMediaGalleryEditor } from '@/components/jobs/job-media-gallery-editor'
import { deriveProcessingFromSchedule, scheduledHoursFromSchedule, splitJobDateTime } from '@/lib/job-schedule-form'
import { WorkflowStatusSelect } from '@/components/jobs/workflow-status-select'
import { useGoogleMapsLoader } from '@/hooks/use-google-maps-loader'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { buildMapSearchDetails, getGooglePlaceCity, getMapSearchAddress, getMapSearchCity } from '@/lib/map-location'
import { cn } from '@/lib/utils'
import { StandaloneSearchBox } from '@react-google-maps/api'
import eventTagsApi from '@/services/event-tags'
import { EventTagsSelect } from '@/components/events/event-tags-select'

type LocationSearchOption = {
  id: string
  label: string
  lat: string
  lon: string
  details: string
  city: string
  address: string
}

const coordinateSchema = z
  .string()
  .trim()
  .optional()
  .refine((value) => value === undefined || value === '' || Number.isFinite(Number(value)), {
    message: 'Enter a valid number',
  })

const formatCoordinate = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toFixed(6) : ''
}

const jobSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  job_code: z.string().optional(),
  job_landmark: z.string().optional(),
  order_reference_id: z.string().optional(),
  recurrence_rule: z.enum(['none', 'daily', 'weekly', 'bi_weekly', 'monthly', 'every_n_days', 'weekly_days', 'monthly_day']).default('none'),
  recurrence_interval_days: z.string().optional(),
  recurrence_weekdays: z.array(z.number().int().min(0).max(6)).default([]),
  recurrence_month_day: z.string().optional(),
  recurrence_count: z.string().optional(),
  recurrence_end: z.string().optional(),
  category: z.string().trim().min(1, 'Category is required'),
  tag_ids: z.array(z.string()).default([]),
  primary_worker: z.string().optional(),
  team_template: z.string().optional(),
  staffed_team: z.string().optional(),
  client_user: z.string().optional(),
  client_name: z.string().trim().optional(),
  client_contact: z.string().optional(),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().trim().optional(),
  location_id: z.string().optional(),
  location: z.string().optional(),
  city: z.string().optional(),
  latitude: coordinateSchema,
  longitude: coordinateSchema,
  address: z.string().optional(),
  priority: z.string(),
  start_date: z.string().optional(),
  start_time: z.string().optional(),
  end_date: z.string().optional(),
  end_time: z.string().optional(),
  processing_time: z.string().optional(),
  processing_unit: z.enum(['min', 'hr', 'day']).default('hr'),
  estimated_hours: z.coerce.number().optional(),
  budget: z.coerce.number().optional(),
  payment_due: z.coerce.number().optional(),
  advance_payment: z.coerce.number().optional(),
  assignment_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_approval_mode: z.enum(['manual', 'auto']).default('manual'),
}).superRefine((data, ctx) => {
  if (data.recurrence_rule === 'every_n_days') {
    const interval = Number(data.recurrence_interval_days)
    if (!Number.isFinite(interval) || interval <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the number of days between recurring jobs.',
        path: ['recurrence_interval_days'],
      })
    }
  }
  if (data.recurrence_rule === 'weekly_days' && data.recurrence_weekdays.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select at least one weekday.',
      path: ['recurrence_weekdays'],
    })
  }
  if (data.recurrence_rule === 'monthly_day') {
    const day = Number(data.recurrence_month_day)
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a day of month between 1 and 31.',
        path: ['recurrence_month_day'],
      })
    }
  }
  if (data.recurrence_rule !== 'none' && data.recurrence_count) {
    const count = Number(data.recurrence_count)
    if (!Number.isInteger(count) || count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Occurrences must be a positive number.',
        path: ['recurrence_count'],
      })
    }
  }
})

type JobFormData = z.infer<typeof jobSchema>

function buildJobFormValues(job: Job): JobFormData {
  const startParts = splitJobDateTime(job.start_date ?? job.scheduled_start)
  const endParts = splitJobDateTime(job.end_date ?? job.scheduled_end)
  const processing = deriveProcessingFromSchedule(
    job.start_date ?? job.scheduled_start,
    job.end_date ?? job.scheduled_end,
    job.scheduled_hours ?? job.estimated_hours,
  )

  return {
    title: job.title,
    description: job.description || '',
    job_code: job.job_code || '',
    job_landmark: job.job_landmark || '',
    order_reference_id: job.order_reference_id || '',
    recurrence_rule: job.recurrence_rule || 'none',
    recurrence_interval_days: job.recurrence_interval_days != null ? String(job.recurrence_interval_days) : '',
    recurrence_weekdays: job.recurrence_weekdays ?? [],
    recurrence_month_day: job.recurrence_month_day != null ? String(job.recurrence_month_day) : '',
    recurrence_count: job.recurrence_count != null ? String(job.recurrence_count) : '',
    recurrence_end: job.recurrence_end || '',
    category: job.category ? String(job.category) : '',
    tag_ids: (job.tags ?? []).map((tag) => String(tag.id)),
    primary_worker: job.primary_worker ? String(job.primary_worker) : 'unassigned',
    team_template: job.team_template ? String(job.team_template) : 'none',
    staffed_team: job.staffed_team ? String(job.staffed_team) : 'none',
    client_user: job.client_user ? String(job.client_user) : 'none',
    client_name: job.client_name || '',
    client_contact: job.client_contact || '',
    email: job.email || '',
    phone: job.phone || '',
    location_id: job.location_id ? String(job.location_id) : 'none',
    location: job.location || '',
    city: job.city || '',
    latitude: formatCoordinate(job.latitude ?? job.location_info?.latitude),
    longitude: formatCoordinate(job.longitude ?? job.location_info?.longitude),
    address: job.address || '',
    priority: job.priority,
    start_date: startParts.date,
    start_time: startParts.time || '09:00',
    end_date: endParts.date,
    end_time: endParts.time,
    processing_time: processing.processing_time,
    processing_unit: processing.processing_unit,
    estimated_hours: job.estimated_hours,
    budget: job.budget,
    payment_due: job.payment_due,
    advance_payment: job.advance_payment,
    assignment_acceptance_mode: job.assignment_acceptance_mode ?? 'manual',
    workday_entry_acceptance_mode: job.workday_entry_acceptance_mode ?? 'manual',
    workday_entry_approval_mode: job.workday_entry_approval_mode ?? 'manual',
  }
}

interface JobEditFormProps {
  job: Job
  serviceInstanceId?: string | null
  entityType?: 'job' | 'project' | 'event'
  onSuccess: () => void
}

export function JobEditForm({ job, serviceInstanceId, entityType = 'job', onSuccess }: JobEditFormProps) {
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const resolvedEntityType = entityType === 'event' ? 'event' : entityType
  const isEventMode = resolvedEntityType === 'event'
  const entityLabel = resolvedEntityType === 'project' ? 'Project' : resolvedEntityType === 'event' ? labels.entity : 'Job'
  const [selectedOrder, setSelectedOrder] = useState(job.order_info ?? null)
  const [locationSearchValue, setLocationSearchValue] = useState(job.location || job.address || '')
  const [appliedLocationSearchValue, setAppliedLocationSearchValue] = useState(job.location || job.address || '')
  const [locationSearchOptions, setLocationSearchOptions] = useState<LocationSearchOption[]>([])
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const [searchBox, setSearchBox] = useState<google.maps.places.SearchBox | null>(null)
  const resolvedServiceInstanceId = useMemo(() => {
    const explicit = String(serviceInstanceId ?? '').trim()
    if (explicit) {
      return explicit
    }

    const jobRecord = job as Job & {
      service_instance?: string | number | null
      service_instance_id?: string | number | null
      job_service_instance_id?: string | number | null
    }
    const fromJob = String(
      jobRecord.service_instance
      ?? jobRecord.service_instance_id
      ?? jobRecord.job_service_instance_id
      ?? job.location_info?.service_instance
      ?? '',
    ).trim()
    if (fromJob) {
      return fromJob
    }

    return getServiceInstanceIdFromSearch(window.location.search)
  }, [job, serviceInstanceId])
  const {
    mapsAvailable,
    mapProvider,
    statusMessage: mapsStatusMessage,
  } = useGoogleMapsLoader({ serviceInstanceReference: resolvedServiceInstanceId })
  const useBackendLocationSearch = mapProvider === 'openstreetmap' || !mapsAvailable
  const { data: categoriesResponse, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['job-categories', entityType],
    queryFn: () => categoriesApi.getCategories({ kind: entityType === 'project' ? 'project' : 'job' }),
  })
  const categories = categoriesResponse?.results ?? []
  const { data: eventTags = [], isLoading: isEventTagsLoading } = useQuery({
    queryKey: ['event-tags', resolvedServiceInstanceId ?? 'none'],
    queryFn: () => eventTagsApi.list(),
    enabled: isEventMode && Boolean(resolvedServiceInstanceId),
  })

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: buildJobFormValues(job),
  })

  useEffect(() => {
    reset(buildJobFormValues(job))
    const initialLocationSearchValue = job.location || job.address || ''
    setLocationSearchValue(initialLocationSearchValue)
    setAppliedLocationSearchValue(initialLocationSearchValue)
  }, [job, reset])

  const selectedCategory = watch('category')
  const selectedPrimaryWorker = watch('primary_worker')
  const selectedTeamTemplate = watch('team_template')
  const selectedStaffedTeam = watch('staffed_team')
  const selectedClientUser = watch('client_user')
  const selectedLocationId = watch('location_id')
  const latitudeValue = watch('latitude')
  const longitudeValue = watch('longitude')
  const recurrenceRule = watch('recurrence_rule')
  const getWorkerSelectValue = (worker: { id?: string | number | null }) => String(worker.id ?? '')
  const { data: workersResponse, isLoading: isWorkersLoading } = useQuery({
    queryKey: ['job-primary-workers', selectedCategory || 'all', serviceInstanceId ?? 'none'],
    queryFn: async () => {
      const scoped = await workersApi.getWorkers({
        page_size: 200,
        worker_status: 'active',
        category: selectedCategory || undefined,
        service_instance_id: serviceInstanceId ?? undefined,
      })

      if ((scoped.results ?? []).length > 0 || !selectedCategory) {
        return scoped
      }

      return workersApi.getWorkers({
        page_size: 200,
        worker_status: 'active',
        service_instance_id: serviceInstanceId ?? undefined,
      })
    },
  })
  const { data: selectedPrimaryWorkerDetails } = useQuery({
    queryKey: ['worker', selectedPrimaryWorker, 'job-edit-primary-worker'],
    queryFn: () => workersApi.getWorker(selectedPrimaryWorker!),
    enabled: Boolean(selectedPrimaryWorker && selectedPrimaryWorker !== 'unassigned' && !workersResponse?.results?.some((worker) => getWorkerSelectValue(worker as { id?: string | number | null }) === selectedPrimaryWorker)),
    staleTime: 30_000,
  })
  const { data: teamTemplates = [], isLoading: isTeamTemplatesLoading } = useQuery({
    queryKey: ['job-team-templates', resolvedServiceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listTemplates({
      service_instance_id: resolvedServiceInstanceId ?? undefined,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 60_000,
  })
  const { data: staffedTeams = [], isLoading: isStaffedTeamsLoading } = useQuery({
    queryKey: ['job-staffed-teams', resolvedServiceInstanceId ?? 'none', selectedTeamTemplate ?? 'none'],
    queryFn: () => crewsApi.listCrews({
      service_instance_id: resolvedServiceInstanceId ?? undefined,
      crew_template_id: selectedTeamTemplate !== 'none' ? selectedTeamTemplate : undefined,
      ready: true,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(resolvedServiceInstanceId && selectedTeamTemplate && selectedTeamTemplate !== 'none'),
    staleTime: 30_000,
  })
  const { data: clientsResponse, isLoading: isClientsLoading } = useQuery({
    queryKey: ['job-clients'],
    queryFn: () => orgUsersApi.listClients(),
    staleTime: 60_000,
  })
  const { data: locationsResponse = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ['job-locations', resolvedServiceInstanceId ?? 'none'],
    queryFn: () => locationsApi.list({
      service_instance_id: resolvedServiceInstanceId ?? undefined,
    }),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 60_000,
  })
  const workers = (() => {
    const results = workersResponse?.results ?? []

    if (
      selectedPrimaryWorkerDetails
      && selectedPrimaryWorker
      && selectedPrimaryWorker !== 'unassigned'
      && !results.some((worker) => getWorkerSelectValue(worker as { id?: string | number | null }) === selectedPrimaryWorker)
    ) {
      return [selectedPrimaryWorkerDetails, ...results]
    }

    return results
  })()
  const clients = clientsResponse ?? []
  const locations = locationsResponse

  const primaryWorkerOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'No primary worker', keywords: 'unassigned none' },
      ...workers.map((worker) => ({
        value: getWorkerSelectValue(worker as { id?: string | number | null }),
        label: getUserDropdownLabel(worker, worker.id),
        keywords: getUserDropdownKeywords(worker),
      })),
    ],
    [workers],
  )

  const clientSelectOptions = useMemo(
    () => [
      { value: 'none', label: 'No linked client', keywords: 'none unlinked' },
      ...clients.map((client) => ({
        value: String(client.id),
        label: [client.fname, client.lname].filter(Boolean).join(' ').trim() || `Client ${client.id}`,
        keywords: `${client.fname || ''} ${client.lname || ''} ${client.email || ''} ${client.phone || ''} ${client.id}`.trim(),
      })),
    ],
    [clients],
  )
  const teamTemplateOptions = useMemo(
    () => [
      { value: 'none', label: 'No team template', keywords: 'none unassigned' },
      ...teamTemplates.map((template) => ({
        value: String(template.id),
        label: template.name,
        keywords: `${template.name ?? ''} ${template.code ?? ''}`.trim(),
      })),
    ],
    [teamTemplates],
  )
  const staffedTeamOptions = useMemo(
    () => [
      { value: 'none', label: 'No staffed team', keywords: 'none unassigned' },
      ...staffedTeams.map((team) => ({
        value: String(team.id),
        label: team.name,
        keywords: `${team.name ?? ''} ${team.code ?? ''}`.trim(),
      })),
    ],
    [staffedTeams],
  )

  useEffect(() => {
    if (
      !isWorkersLoading &&
      selectedPrimaryWorker &&
      selectedPrimaryWorker !== 'unassigned' &&
      !workers.some((worker) => getWorkerSelectValue(worker as { id?: string | number | null }) === selectedPrimaryWorker)
    ) {
      setValue('primary_worker', 'unassigned')
    }
  }, [isWorkersLoading, selectedCategory, selectedPrimaryWorker, setValue, workers])

  useEffect(() => {
    if (!selectedTeamTemplate || selectedTeamTemplate === 'none') {
      if (selectedStaffedTeam && selectedStaffedTeam !== 'none') {
        setValue('staffed_team', 'none')
      }
      return
    }

    if (
      !isStaffedTeamsLoading
      && selectedStaffedTeam
      && selectedStaffedTeam !== 'none'
      && !staffedTeams.some((team) => String(team.id) === selectedStaffedTeam)
    ) {
      setValue('staffed_team', 'none')
    }
  }, [isStaffedTeamsLoading, selectedStaffedTeam, selectedTeamTemplate, setValue, staffedTeams])

  useEffect(() => {
    if (!selectedClientUser || selectedClientUser === 'none') {
      return
    }

    const client = clients.find((item) => String(item.id) === selectedClientUser)
    if (!client) {
      return
    }

    const fullName = [client.fname, client.lname].filter(Boolean).join(' ').trim()
    const email = client.email || client.email_data?.find((entry) => entry.email)?.email || ''
    const phone = client.phone || client.phonenumber_data?.find((entry) => entry.mobile_number)?.mobile_number || ''

    setValue('client_name', fullName || '', { shouldDirty: true })
    setValue('email', email, { shouldDirty: true, shouldValidate: true })
    setValue('phone', phone, { shouldDirty: true })
  }, [clients, selectedClientUser, setValue])

  useEffect(() => {
    if (!selectedLocationId || selectedLocationId === 'none') {
      return
    }

    const selectedLocation = locations.find((item) => String(item.id) === selectedLocationId)
    if (!selectedLocation) {
      if (!isLocationsLoading) {
        setValue('location_id', 'none', { shouldDirty: true })
      }
      return
    }

    setValue('location', selectedLocation.place, { shouldDirty: true })
    setLocationSearchValue(selectedLocation.place)
  setAppliedLocationSearchValue(selectedLocation.place)
    setValue('latitude', formatCoordinate(selectedLocation.latitude), { shouldDirty: true, shouldValidate: true })
    setValue('longitude', formatCoordinate(selectedLocation.longitude), { shouldDirty: true, shouldValidate: true })

    if (selectedLocation.client_user && String(selectedLocation.client_user) !== selectedClientUser) {
      setValue('client_user', String(selectedLocation.client_user), { shouldDirty: true })
    }
  }, [isLocationsLoading, locations, selectedClientUser, selectedLocationId, setValue])

  useEffect(() => {
    if (!useBackendLocationSearch) {
      setLocationSearchOptions([])
      setIsSearchingLocation(false)
      return
    }

    const query = locationSearchValue.trim()
    if (query.length < 3 || !resolvedServiceInstanceId) {
      setLocationSearchOptions([])
      setIsSearchingLocation(false)
      return
    }

    if (query === appliedLocationSearchValue.trim()) {
      setLocationSearchOptions([])
      setIsSearchingLocation(false)
      return
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearchingLocation(true)
        const payload = await orgTreeApi.searchServiceInstanceLocation(resolvedServiceInstanceId, query, 5)
        setLocationSearchOptions(
          (payload.results ?? [])
            .map((entry) => ({
              id: String(entry.id ?? '').trim(),
              label: String(entry.label ?? '').trim(),
              lat: String(entry.lat ?? '').trim(),
              lon: String(entry.lon ?? '').trim(),
              city: getMapSearchCity(entry),
              address: getMapSearchAddress(entry),
              details: buildMapSearchDetails(entry),
            }))
            .filter((entry) => entry.id && entry.label && entry.lat && entry.lon),
        )
      } catch {
        setLocationSearchOptions([])
      } finally {
        setIsSearchingLocation(false)
      }
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [appliedLocationSearchValue, locationSearchValue, resolvedServiceInstanceId, useBackendLocationSearch])

  const applyLocationSearchResult = useCallback((params: {
    label: string
    latitude: string | number
    longitude: string | number
    city?: string | null
    address?: string | null
  }) => {
    const label = params.label.trim()
    const address = String(params.address ?? label).trim()
    const city = String(params.city ?? '').trim()
    const lat = formatCoordinate(params.latitude)
    const lng = formatCoordinate(params.longitude)
    if (!lat || !lng) {
      return
    }

    setLocationSearchValue(label)
  setAppliedLocationSearchValue(label)
    setLocationSearchOptions([])
    setValue('location', label, { shouldDirty: true, shouldValidate: true })
    setValue('city', city, { shouldDirty: true, shouldValidate: true })
    setValue('address', address, { shouldDirty: true })
    setValue('latitude', lat, { shouldDirty: true, shouldValidate: true })
    setValue('longitude', lng, { shouldDirty: true, shouldValidate: true })
  }, [setValue])

  const handleGooglePlaceChanged = useCallback(() => {
    const place = searchBox?.getPlaces()?.[0]
    const selectedPlace = place?.geometry?.location
    if (!selectedPlace) {
      return
    }

    applyLocationSearchResult({
      label: place.formatted_address || place.name || '',
      address: place.formatted_address || place.name || '',
      city: getGooglePlaceCity(place),
      latitude: selectedPlace.lat(),
      longitude: selectedPlace.lng(),
    })
  }, [applyLocationSearchResult, searchBox])

  const updateMutation = useMutation({
    mutationFn: (data: JobFormData) => {
      const normalizedPrimaryWorker = data.primary_worker && data.primary_worker !== 'unassigned'
        ? String(data.primary_worker)
        : null

      return (entityType === 'project' ? jobsApi.updateProject : jobsApi.update)(job.id, {
        ...data,
        job_code: data.job_code?.trim() || null,
        job_landmark: data.job_landmark?.trim() || null,
        estimated_hours: scheduledHoursFromSchedule(
          data.start_date,
          data.start_time,
          data.end_date,
          data.end_time,
          data.processing_time,
          data.processing_unit,
        ),
        recurrence_interval_days: data.recurrence_rule === 'every_n_days' && data.recurrence_interval_days
          ? Number(data.recurrence_interval_days)
          : null,
        recurrence_weekdays: data.recurrence_rule === 'weekly_days' ? data.recurrence_weekdays : [],
        recurrence_month_day: data.recurrence_rule === 'monthly_day' && data.recurrence_month_day
          ? Number(data.recurrence_month_day)
          : null,
        recurrence_count: data.recurrence_rule !== 'none' && data.recurrence_count
          ? Number(data.recurrence_count)
          : null,
        recurrence_end: data.recurrence_rule !== 'none' && data.recurrence_end ? data.recurrence_end : null,
        order_reference_id: data.order_reference_id?.trim() || null,
        primary_worker: normalizedPrimaryWorker,
        team_template: data.team_template && data.team_template !== 'none' ? data.team_template : null,
        staffed_team: data.staffed_team && data.staffed_team !== 'none' ? data.staffed_team : null,
        client_user: data.client_user && data.client_user !== 'none' ? data.client_user : null,
        location_id: data.location_id && data.location_id !== 'none' ? data.location_id : null,
        tag_ids: isEventMode
          ? data.tag_ids.map((tagId) => Number(tagId)).filter((tagId) => Number.isFinite(tagId))
          : undefined,
      })
    },
    onSuccess: (updatedJob) => {
      toast.success(`${entityLabel} updated successfully`)
      queryClient.setQueryData(['job', String(job.id), serviceInstanceId ?? 'none'], updatedJob)
      queryClient.invalidateQueries({ queryKey: ['job', String(job.id)] })
      queryClient.invalidateQueries({ queryKey: ['job', job.id] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', String(job.id)] })
      queryClient.invalidateQueries({ queryKey: ['invoice-preview', job.id] })
      onSuccess()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: JobFormData) => {
    updateMutation.mutate(data)
  }

  const priority = watch('priority')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Basic Information</CardTitle>
            <CardDescription>{`Update ${entityLabel.toLowerCase()} details`}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">{`${entityLabel} Title *`}</Label>
              <Input
                id="title"
                placeholder={`Enter ${entityLabel.toLowerCase()} title`}
                {...register('title')}
                aria-invalid={!!errors.title}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="job_code">Code</Label>
                <Input
                  id="job_code"
                  placeholder="Optional business code"
                  {...register('job_code')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="job_landmark">Landmark</Label>
                <Input
                  id="job_landmark"
                  placeholder="Optional landmark or venue"
                  {...register('job_landmark')}
                />
              </div>
            </div>

            <div className={cn('gap-4', isEventMode ? 'grid sm:grid-cols-2' : 'space-y-2')}>
              <div className="space-y-2">
                <Label htmlFor="category">{isEventMode ? 'Event Category *' : 'Category *'}</Label>
                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={isCategoriesLoading || categories.length === 0}>
                      <SelectTrigger id="category" aria-invalid={!!errors.category}>
                        <SelectValue placeholder={isCategoriesLoading ? 'Loading categories...' : 'Select category'} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={String(category.id)}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.category && (
                  <p className="text-sm text-destructive">{errors.category.message}</p>
                )}
              </div>

              {isEventMode ? (
                <div className="space-y-2">
                  <Label htmlFor="event-tags">Tags</Label>
                  <Controller
                    control={control}
                    name="tag_ids"
                    render={({ field }) => (
                      <EventTagsSelect
                        id="event-tags"
                        value={field.value ?? []}
                        onValueChange={field.onChange}
                        tags={eventTags}
                        disabled={isEventTagsLoading || !resolvedServiceInstanceId}
                        placeholder={isEventTagsLoading ? 'Loading tags...' : 'Select tags'}
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter job description"
                rows={4}
                {...register('description')}
              />
            </div>

            <OrderSelector
              value={watch('order_reference_id')}
              selectedOrder={selectedOrder}
              onSelect={(order) => {
                setSelectedOrder(order)
                setValue('order_reference_id', order?.reference_id ?? '', { shouldDirty: true, shouldValidate: true })
              }}
              error={errors.order_reference_id?.message}
            />

            <div className="space-y-2">
              <Label htmlFor="primary_worker">Incharge</Label>
              <Controller
                control={control}
                name="primary_worker"
                render={({ field }) => (
                  <SearchableSelect
                    id="primary_worker"
                    ariaLabel="Primary worker"
                    value={field.value || 'unassigned'}
                    onValueChange={field.onChange}
                    options={primaryWorkerOptions}
                    placeholder={isWorkersLoading ? 'Loading workers...' : 'Select primary worker'}
                    searchPlaceholder="Search workers"
                    emptyText="No workers found."
                    disabled={isWorkersLoading}
                  />
                )}
              />
              {!isWorkersLoading && workers.length === 0 && (
                <p className="text-sm text-muted-foreground">No active workers found for this service instance.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="team_template">Team Template</Label>
              <Controller
                control={control}
                name="team_template"
                render={({ field }) => (
                  <SearchableSelect
                    id="team_template"
                    ariaLabel="Team template"
                    value={field.value || 'none'}
                    onValueChange={field.onChange}
                    options={teamTemplateOptions}
                    placeholder={isTeamTemplatesLoading ? 'Loading team templates...' : 'Select team template'}
                    searchPlaceholder="Search team templates"
                    emptyText="No team templates found."
                    disabled={!resolvedServiceInstanceId || isTeamTemplatesLoading}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="staffed_team">Staffed Team</Label>
              <Controller
                control={control}
                name="staffed_team"
                render={({ field }) => (
                  <SearchableSelect
                    id="staffed_team"
                    ariaLabel="Staffed team"
                    value={field.value || 'none'}
                    onValueChange={field.onChange}
                    options={staffedTeamOptions}
                    placeholder={selectedTeamTemplate === 'none' ? 'Select team template first' : isStaffedTeamsLoading ? 'Loading staffed teams...' : 'Select staffed team'}
                    searchPlaceholder="Search staffed teams"
                    emptyText="No staffed teams found."
                    disabled={!resolvedServiceInstanceId || selectedTeamTemplate === 'none' || isStaffedTeamsLoading}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <WorkflowStatusSelect
                  entityType="job"
                  entityId={job.id}
                  currentValue={job.workflow_status_label || 'Workflow not set'}
                  disabled={updateMutation.isPending}
                  className="w-full"
                  onApplied={() => {
                    queryClient.invalidateQueries({ queryKey: ['job', job.id] })
                    queryClient.invalidateQueries({ queryKey: ['jobs'] })
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={priority}
                  onValueChange={(value) => setValue('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Client Information</CardTitle>
              <CardDescription>Pick a registered client, type an unregistered client, or leave this section empty.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client_user">Registered client</Label>
              <Controller
                control={control}
                name="client_user"
                render={({ field }) => (
                  <SearchableSelect
                    id="client_user"
                    ariaLabel="Registered client"
                    value={field.value || 'none'}
                    onValueChange={field.onChange}
                    options={clientSelectOptions}
                    placeholder={isClientsLoading ? 'Loading clients...' : 'Select client (optional)'}
                    searchPlaceholder="Search clients"
                    emptyText="No clients found."
                    disabled={isClientsLoading}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name</Label>
              <Input
                id="client_name"
                placeholder="Enter client name"
                {...register('client_name')}
                aria-invalid={!!errors.client_name}
              />
              {errors.client_name && (
                <p className="text-sm text-destructive">{errors.client_name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Client Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="client@example.com"
                  {...register('email')}
                  aria-invalid={!!errors.email}
                />
                {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Client Phone</Label>
                <Input
                  id="phone"
                  placeholder="9876543210"
                  {...register('phone')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_id">Saved Location</Label>
              <Controller
                control={control}
                name="location_id"
                render={({ field }) => (
                  <Select value={field.value || 'none'} onValueChange={field.onChange} disabled={isLocationsLoading}>
                    <SelectTrigger id="location_id">
                      <SelectValue placeholder={isLocationsLoading ? 'Loading locations...' : 'Select saved location (optional)'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No linked location</SelectItem>
                        {!isLocationsLoading && locations.length === 0 ? (
                          <SelectItem value="__no_saved_locations" disabled>No saved locations found</SelectItem>
                        ) : null}
                      {locations.map((savedLocation) => (
                        <SelectItem key={savedLocation.id} value={String(savedLocation.id)}>
                          {savedLocation.place}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="relative space-y-2">
              <Label htmlFor="job-location-search">Find a place</Label>
              {useBackendLocationSearch ? (
                <Input
                  id="job-location-search"
                  placeholder={resolvedServiceInstanceId ? 'Search for a place or address' : 'Service instance required for location search'}
                  value={locationSearchValue}
                  onChange={(event) => {
                    setAppliedLocationSearchValue('')
                    setLocationSearchValue(event.target.value)
                  }}
                  disabled={!resolvedServiceInstanceId}
                />
              ) : mapsAvailable ? (
                <StandaloneSearchBox
                  onLoad={(ref) => setSearchBox(ref)}
                  onPlacesChanged={handleGooglePlaceChanged}
                >
                  <Input
                    id="job-location-search"
                    placeholder="Search for a place or address"
                    value={locationSearchValue}
                    onChange={(event) => {
                      setAppliedLocationSearchValue('')
                      setLocationSearchValue(event.target.value)
                    }}
                  />
                </StandaloneSearchBox>
              ) : (
                <Input
                  id="job-location-search"
                  placeholder="Search unavailable until map settings load"
                  value={locationSearchValue}
                  onChange={(event) => {
                    setAppliedLocationSearchValue('')
                    setLocationSearchValue(event.target.value)
                  }}
                />
              )}
              {locationSearchOptions.length > 0 ? (
                <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-border/70 bg-popover p-1 shadow-lg">
                  {locationSearchOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => applyLocationSearchResult({
                        label: option.label,
                        address: option.address || option.details || option.label,
                        city: option.city,
                        latitude: option.lat,
                        longitude: option.lon,
                      })}
                    >
                      <span className="block font-medium text-foreground">{option.label}</span>
                      {option.details ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{option.details}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {isSearchingLocation
                  ? 'Searching places...'
                  : !resolvedServiceInstanceId
                    ? 'Select a service instance to search locations.'
                    : useBackendLocationSearch && mapProvider === 'googlemaps'
                      ? mapsStatusMessage || 'Using address search fallback until Google Places is ready.'
                      : 'Search updates the job address, latitude, and longitude.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="Job location"
                {...register('location')}
              />
              {latitudeValue || longitudeValue ? (
                <p className="text-[10px] leading-4 text-muted-foreground">
                  Lat: {latitudeValue || '-'} · Lan: {longitudeValue || '-'}
                </p>
              ) : null}
            </div>

            <input type="hidden" {...register('latitude')} />
            <input type="hidden" {...register('longitude')} />
            {errors.latitude ? <p className="text-sm text-destructive">{errors.latitude.message}</p> : null}
            {errors.longitude ? <p className="text-sm text-destructive">{errors.longitude.message}</p> : null}

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                placeholder="Full address"
                rows={2}
                {...register('address')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Schedule</CardTitle>
            <CardDescription>Update job timeline</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <JobScheduleFields {...bindJobScheduleFormFields({ control, register, setValue, errors })} />

            <div className="space-y-2">
              <Label htmlFor="recurrence_rule">Recurrence</Label>
              <Controller
                control={control}
                name="recurrence_rule"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="recurrence_rule">
                      <SelectValue placeholder="Select recurrence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Does not recur</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="every_n_days">After specific number of days</SelectItem>
                      <SelectItem value="weekly_days">Weekly on selected days</SelectItem>
                      <SelectItem value="monthly_day">Monthly on a specific day</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {recurrenceRule === 'every_n_days' && (
              <div className="space-y-2">
                <Label htmlFor="recurrence_interval_days">Repeat every N days</Label>
                <Input
                  id="recurrence_interval_days"
                  type="number"
                  min="1"
                  placeholder="Enter days"
                  {...register('recurrence_interval_days')}
                />
                {errors.recurrence_interval_days && (
                  <p className="text-sm text-destructive">{errors.recurrence_interval_days.message}</p>
                )}
              </div>
            )}

            {recurrenceRule === 'weekly_days' && (
              <div className="space-y-2">
                <Label>Repeat on these days</Label>
                <Controller
                  control={control}
                  name="recurrence_weekdays"
                  render={({ field }) => (
                    <div className="flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, dayIndex) => {
                        const isSelected = field.value.includes(dayIndex)
                        return (
                          <Button
                            key={label}
                            type="button"
                            size="sm"
                            variant={isSelected ? 'default' : 'outline'}
                            onClick={() =>
                              field.onChange(
                                isSelected
                                  ? field.value.filter((day) => day !== dayIndex)
                                  : [...field.value, dayIndex].sort((a, b) => a - b)
                              )
                            }
                          >
                            {label}
                          </Button>
                        )
                      })}
                    </div>
                  )}
                />
                {errors.recurrence_weekdays && (
                  <p className="text-sm text-destructive">{errors.recurrence_weekdays.message}</p>
                )}
              </div>
            )}

            {recurrenceRule === 'monthly_day' && (
              <div className="space-y-2">
                <Label htmlFor="recurrence_month_day">Day of month</Label>
                <Input
                  id="recurrence_month_day"
                  type="number"
                  min="1"
                  max="31"
                  placeholder="e.g. 15"
                  {...register('recurrence_month_day')}
                />
                {errors.recurrence_month_day && (
                  <p className="text-sm text-destructive">{errors.recurrence_month_day.message}</p>
                )}
              </div>
            )}

            {recurrenceRule !== 'none' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="recurrence_end">Repeat until</Label>
                  <Input
                    id="recurrence_end"
                    type="date"
                    {...register('recurrence_end')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recurrence_count">Number of occurrences (optional)</Label>
                  <Input
                    id="recurrence_count"
                    type="number"
                    min="1"
                    placeholder="Leave blank for open-ended"
                    {...register('recurrence_count')}
                  />
                  {errors.recurrence_count && (
                    <p className="text-sm text-destructive">{errors.recurrence_count.message}</p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Budget */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financials</CardTitle>
            <CardDescription>Update customer payable and job budget</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payment_due">Customer Payable</Label>
              <Input
                id="payment_due"
                type="number"
                placeholder="0.00"
                step="0.01"
                {...register('payment_due')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget">Budget Amount</Label>
              <Input
                id="budget"
                type="number"
                placeholder="0.00"
                step="0.01"
                {...register('budget')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="advance_payment">Advance Payment</Label>
              <Input
                id="advance_payment"
                type="number"
                placeholder="0.00"
                step="0.01"
                {...register('advance_payment')}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <JobAcceptanceModeFields {...bindJobAcceptanceModeFormFields({ control })} />

      <JobMediaGalleryEditor
        job={job}
        title="Job Gallery"
        description="Upload images, videos, and supporting files while editing this job."
      />

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <Button type="button" variant="outline" onClick={onSuccess}>
          Cancel
        </Button>
        <Button type="submit" disabled={updateMutation.isPending || isCategoriesLoading || categories.length === 0}>
          {updateMutation.isPending && <Spinner className="mr-2" />}
          Save Changes
        </Button>
      </div>
    </form>
  )
}
